// Lazy-forwarding Sentry shim.
//
// Historically this was a pure no-op. Importing `@sentry/nextjs` synchronously
// from a module reachable during build/prerender makes
// `@opentelemetry/sdk-trace-base` fail ("Cannot read properties of undefined
// (reading 'AlwaysOn')"). A 2026-06-17 fix resolved exactly that by lazy-loading
// Sentry out of audio-player-context / swr-config / error-boundary. See TES-1430.
//
// The no-op version silently discarded every `captureException` / `captureMessage`
// across ~59 files, voiding server-side error reporting. This module now FORWARDS
// each call to the real SDK via a runtime dynamic `import()`, keeping the exact
// synchronous, build-safe call signature so no call site has to change.
//
// Contract:
//  - Fire-and-forget: callers never await; a call returns `void` synchronously.
//  - No-op during the production build phase (`NEXT_PHASE`) and when no DSN is
//    configured, preserving the prior build/prerender safety.
//  - Any dynamic-import rejection (Sentry outage, missing module) is swallowed so
//    it can never surface as an application error.
//  - The resolved module is cached, so repeated calls do not re-import.
//
// Do NOT add a synchronous top-level `import * as Sentry from '@sentry/nextjs'`
// to this file — that reintroduces the OpenTelemetry build failure.

type SentryModule = typeof import('@sentry/nextjs')

let cached: SentryModule | null = null
let loading: Promise<SentryModule | null> | null = null

/**
 * True when Sentry must stay a no-op:
 *  - during `next build` / prerender (importing the SDK there is unsafe), or
 *  - when no DSN is set (nothing to report to; mirrors the SDK's own init guard).
 * Read lazily on each call so build-time and runtime evaluate independently.
 */
function sentryDisabled(): boolean {
  if (process.env.NEXT_PHASE === 'phase-production-build') return true
  if (!process.env.NEXT_PUBLIC_SENTRY_DSN && !process.env.SENTRY_DSN) return true
  return false
}

/** Dynamically import `@sentry/nextjs` once, caching the module. Never rejects. */
function loadSentry(): Promise<SentryModule | null> {
  if (cached) return Promise.resolve(cached)
  if (loading) return loading
  loading = import('@sentry/nextjs')
    .then((mod) => {
      cached = mod
      return mod
    })
    .catch(() => null)
  return loading
}

/** Forward a fire-and-forget SDK call. Guarded and non-throwing by construction. */
function forward(method: keyof SentryModule, args: unknown[]): void {
  if (sentryDisabled()) return
  void loadSentry()
    .then((mod) => {
      if (!mod) return
      const fn = (mod as Record<string, unknown>)[method as string]
      if (typeof fn === 'function') {
        try {
          ;(fn as (...a: unknown[]) => unknown)(...args)
        } catch {
          // A capture failing must never break the caller.
        }
      }
    })
    .catch(() => {
      // Swallow any load/forward rejection — a Sentry outage is not an app error.
    })
}

/** A no-op scope so `withScope` callbacks that touch a scope never throw. */
function noopScope(): Record<string, () => void> {
  const noop = () => {}
  return new Proxy({}, { get: () => noop }) as Record<string, () => void>
}

const sentrySafe = {
  captureException: (...args: unknown[]): void => forward('captureException', args),
  captureMessage: (...args: unknown[]): void => forward('captureMessage', args),
  captureEvent: (...args: unknown[]): void => forward('captureEvent', args),
  addBreadcrumb: (...args: unknown[]): void => forward('addBreadcrumb', args),
  setTag: (...args: unknown[]): void => forward('setTag', args),
  setUser: (...args: unknown[]): void => forward('setUser', args),
  setContext: (...args: unknown[]): void => forward('setContext', args),
  // `withScope` takes a callback. When Sentry is live, forward to the real
  // implementation so the callback receives a real scope; otherwise invoke the
  // callback synchronously with a no-op scope (matches prior no-op behavior and
  // keeps any inline capture inside the callback flowing through this shim).
  withScope: (fn: (scope: unknown) => void): void => {
    if (sentryDisabled()) {
      try {
        fn(noopScope())
      } catch {
        /* callback must never throw into the caller */
      }
      return
    }
    void loadSentry()
      .then((mod) => {
        if (mod && typeof mod.withScope === 'function') {
          ;(mod.withScope as (cb: (scope: unknown) => void) => void)(fn)
        } else {
          try {
            fn(noopScope())
          } catch {
            /* no-op */
          }
        }
      })
      .catch(() => {})
  },
} as const

export default sentrySafe as typeof sentrySafe & Record<string, (...args: unknown[]) => void>
